import { Button, Card, Collapse, Form, Input, Modal, Popconfirm, Select, Switch, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined, PlusOutlined, TeamOutlined } from "@ant-design/icons";

import { TemplateAdministrationOffice, TemplateAdministrationOfficeUser } from "../../Models";
import useOfficeUsersAdmin from "../../Hooks/useOfficeUsersAdmin/useOfficeUsersAdmin";

import { IOfficeUsersAdminProps } from "./IOfficeUsersAdmin";

const OfficeUsersAdmin = ({
	loading,
	isReadOnly,
	translations,
	institutions,
	users,
	offices,
	officeUsers,
	handleSaveOffice,
	handleDeleteOffice,
	handleSaveOfficeUser,
	handleDeleteOfficeUser
}: IOfficeUsersAdminProps) => {
	const t = (key: string, fallback: string) => translations[key] || fallback;
	const {
		selectedInstitutionId,
		setSelectedInstitutionId,
		userSearchText,
		setUserSearchText,
		isOfficeModalOpen,
		isOfficeUserModalOpen,
		editingOffice,
		selectedOffice,
		officeForm,
		officeUserForm,
		filteredOfficeUsers,
		groupedOffices,
		openOfficeModal,
		openOfficeUsersModal,
		handleCreateOrUpdateOffice,
		handleCreateOfficeUser,
		closeOfficeModal,
		closeOfficeUsersModal
	} = useOfficeUsersAdmin({
		offices,
		officeUsers,
		handleSaveOffice,
		handleSaveOfficeUser
	});

	const officeColumns: ColumnsType<TemplateAdministrationOffice> = [
		{
			title: t("institution", "Institución"),
			dataIndex: "institutionName",
			key: "institutionName",
			width: 280
		},
		{
			title: t("office", "Oficina"),
			dataIndex: "description",
			key: "description",
			render: (_, record) => (
				<div className="templates-details-summary">{record.description}</div>
			)
		},
		{
			title: t("details", "Detalles"),
			key: "details",
			render: (_, record) => (
				<div className="templates-details-cell">
					<div className="templates-details-summary">
						{t("adminOfficeHeadquarter", "Sede principal")}: {record.isHeadquarter ? t("si", "Sí") : t("no", "No")}
					</div>
					<div className="templates-details-muted">
						{t("adminUsers", "Usuarios")}: {record.userCount}
					</div>
				</div>
			)
		},
		{
			title: t("actions", "Acciones"),
			key: "actions",
			width: 260,
			render: (_, record) => (
				<div className="institution-vehicle-actions">
					<Button
						size="small"
						icon={<TeamOutlined />}
						onClick={() => openOfficeUsersModal(record)}
						disabled={isReadOnly}
					>
						{t("adminUsers", "Usuarios")}
					</Button>
					<Button
						size="small"
						onClick={() => openOfficeModal(record)}
						disabled={isReadOnly}
					>
						{t("edit", "Editar")}
					</Button>
					<Popconfirm
						title={t("adminDeleteTitle", "Eliminar")}
						description={t("adminDeleteOfficeConfirm", "¿Eliminar esta oficina y sus usuarios asignados?")}
						onConfirm={() => handleDeleteOffice(record.id)}
						okText={t("accept", "Aceptar")}
						cancelText={t("cancel", "Cancelar")}
						disabled={isReadOnly}
					>
						<Button
							danger
							size="small"
							icon={<DeleteOutlined />}
							disabled={isReadOnly}
						>
							{t("delete", "Eliminar")}
						</Button>
					</Popconfirm>
				</div>
			)
		}
	];

	const officeUserColumns: ColumnsType<TemplateAdministrationOfficeUser> = [
		{
			title: t("user", "Usuario"),
			dataIndex: "userName",
			key: "userName"
		},
		{
			title: t("actions", "Acciones"),
			key: "actions",
			width: 120,
			render: (_, record) => (
				<Popconfirm
					title={t("adminDeleteTitle", "Eliminar")}
					description={t("adminDeleteOfficeUserConfirm", "¿Eliminar esta asignación de usuario a oficina?")}
					onConfirm={() => handleDeleteOfficeUser(record.id)}
					okText={t("accept", "Aceptar")}
					cancelText={t("cancel", "Cancelar")}
					disabled={isReadOnly}
				>
					<Button
						danger
						size="small"
						icon={<DeleteOutlined />}
						disabled={isReadOnly}
					>
						{t("delete", "Eliminar")}
					</Button>
				</Popconfirm>
			)
		}
	];

	return (
		<>
			<Card
				className="templates-static-card"
				title={t("adminOfficesTitle", "Oficinas")}
				loading={loading}
			>
				<div className="institution-vehicle-toolbar">
					<div className="institution-vehicle-input-group">
						<div className="institution-vehicle-input-label">
							{t("adminInstitutionFilter", "Filtrar por institución")}
						</div>
						<Select
							value={selectedInstitutionId ?? undefined}
							onChange={setSelectedInstitutionId}
							allowClear
							showSearch
							optionFilterProp="label"
							options={institutions.map((institution) => ({
								value: institution.id,
								label: institution.name
							}))}
						/>
					</div>
					<div className="institution-vehicle-input-group">
						<div className="institution-vehicle-input-label">
							{t("adminUserEmailFilter", "Email de usuario")}
						</div>
						<Input
							value={userSearchText}
							onChange={(event) => setUserSearchText(event.target.value)}
							placeholder={t("adminUserEmailFilterPlaceholder", "Filtrar por email de usuario")}
							allowClear
						/>
					</div>
					<div className="institution-vehicle-toolbar-action">
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => openOfficeModal()}
							disabled={isReadOnly}
						>
							{t("add", "Añadir")}
						</Button>
					</div>
				</div>

				<Collapse
					className="template-fields-sections"
					items={groupedOffices.map((group) => ({
						key: group.idInstitution,
						label: `${group.institutionName} (${group.rows.length})`,
						children: (
							<Table
								rowKey="id"
								className="templates-table"
								columns={officeColumns}
								dataSource={group.rows}
								pagination={false}
							/>
						)
					}))}
				/>
			</Card>

			<Modal
				title={editingOffice ? t("adminOfficeEditTitle", "Editar oficina") : t("adminOfficeNewTitle", "Nueva oficina")}
				open={isOfficeModalOpen}
				onOk={handleCreateOrUpdateOffice}
				onCancel={closeOfficeModal}
				okText={t("save", "Guardar")}
				cancelText={t("cancel", "Cancelar")}
				destroyOnClose
			>
				<Form form={officeForm} layout="vertical">
					<Form.Item name="id" hidden>
						<Input />
					</Form.Item>
					<Form.Item
						label={t("institution", "Institución")}
						name="idInstitution"
						rules={[{ required: true, message: t("adminInstitutionRequired", "La institución es obligatoria") }]}
					>
						<Select
							showSearch
							optionFilterProp="label"
							options={institutions.map((institution) => ({
								value: institution.id,
								label: institution.name
							}))}
						/>
					</Form.Item>
					<Form.Item
						label={t("adminOfficeDescription", "Descripción")}
						name="description"
						rules={[{ required: true, message: t("adminDescriptionRequired", "La descripción es obligatoria") }]}
					>
						<Input />
					</Form.Item>
					<Form.Item label={t("adminOfficeHeadquarter", "Sede principal")} name="isHeadquarter" valuePropName="checked">
						<Switch />
					</Form.Item>
				</Form>
			</Modal>

			<Modal
				title={selectedOffice ? `${t("adminOfficeUsersTitle", "Usuarios de oficina")} · ${selectedOffice.description}` : t("adminOfficeUsersTitle", "Usuarios de oficina")}
				open={isOfficeUserModalOpen}
				onOk={handleCreateOfficeUser}
				onCancel={closeOfficeUsersModal}
				okText={t("save", "Guardar")}
				cancelText={t("cancel", "Cancelar")}
				destroyOnClose
				width={900}
			>
				<Form form={officeUserForm} layout="vertical">
					<Form.Item
						name="idOffice"
						hidden
					>
						<Input />
					</Form.Item>
					<div className="institution-vehicle-input-label">
						{t("user", "Usuario")}
					</div>
					<div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
						<Form.Item
							name="idUser"
							rules={[{ required: true, message: t("adminUserRequired", "El usuario es obligatorio") }]}
							style={{ marginBottom: 0, flex: "1 1 auto" }}
						>
							<Select
								showSearch
								optionFilterProp="label"
								style={{ width: "100%" }}
								options={users.map((user) => ({
									value: user.id,
									label: user.name
								}))}
							/>
						</Form.Item>
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={handleCreateOfficeUser}
							disabled={isReadOnly || !selectedOffice}
						>
							{t("add", "Añadir")}
						</Button>
					</div>
				</Form>

				<Table
					rowKey="id"
					className="templates-table"
					columns={officeUserColumns}
					dataSource={filteredOfficeUsers}
					pagination={{ pageSize: 8, showSizeChanger: true }}
				/>
			</Modal>
		</>
	);
};

export default OfficeUsersAdmin;
